import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { LobbySettings } from '@srcbattle/shared';
import { api, ApiRequestError, type AdminLobby, type AdminMember, type GameHistoryEntry } from '../api.ts';
import { useSession } from '../session.tsx';
import { Avatar, Banner, Button, Card, Field, Spinner } from '../components/ui.tsx';
import { CodeReveal } from '../components/CodeReveal.tsx';
import { SettingsControls } from '../components/LobbySettings.tsx';
import { Appearance } from '../components/Appearance.tsx';
import { GameHistory } from '../components/GameHistory.tsx';
import { FeatureRequestsAdmin } from '../components/FeatureRequestsAdmin.tsx';
import { AdminGuests } from '../components/GuestInvites.tsx';

function relativeTime(timestamp: number): string {
  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/**
 * The settings a new lobby starts from.
 *
 * SRC plays the same way most weeks, and re-picking the same eight settings
 * every Friday is the sort of small friction that makes a tool feel like work.
 * These are the same controls as the lobby's, rendered from the same component
 * so the two cannot drift apart.
 */
function DefaultSettings() {
  const [settings, setSettings] = useState<LobbySettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .adminSettings()
      .then(({ settings: current }) => setSettings(current))
      .catch(() => setError('Could not load the defaults.'));
  }, []);

  const save = async (update: Partial<LobbySettings>) => {
    setBusy(true);
    setError(null);
    try {
      const { settings: next } = await api.saveAdminSettings(update);
      setSettings(next);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium">Default lobby settings</h2>
        <Button
          variant="ghost"
          disabled={busy || !settings}
          onClick={() => {
            void api
              .resetAdminSettings()
              .then(({ settings: next }) => setSettings(next))
              .catch(() => setError('Could not reset them.'));
          }}
        >
          Reset
        </Button>
      </div>
      <p className="mb-2 text-xs text-text-faint">Used for new lobbies.</p>

      {error && (
        <div className="my-2">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {settings ? (
        <SettingsControls
          settings={settings}
          locked={busy}
          patch={(update) => void save(update)}
        />
      ) : (
        <p className="text-sm text-text-faint">Loading…</p>
      )}
    </Card>
  );
}

function GlobalFeatures() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  useEffect(() => { void api.superReactionSetting().then((value) => { setEnabled(value.enabled); setDurationMs(value.durationMs); }); }, []);
  return <Card className="mb-6 flex flex-wrap items-end justify-between gap-3">
    <div><h2 className="font-medium">Super messages</h2><p className="text-xs text-text-faint">Image and GIF reactions.</p></div>
    <label className="text-xs text-text-faint">Fade out after
      <select value={durationMs ?? 3000} disabled={durationMs === null} onChange={(event) => { const next = Number(event.target.value); void api.saveSuperReactionSetting({ durationMs: next }).then((value) => setDurationMs(value.durationMs)); }} className="ml-2 rounded-lg border border-border bg-surface px-2 py-1 text-sm text-text">
        {[1000, 2000, 3000, 5000, 8000, 10000].map((value) => <option key={value} value={value}>{value / 1000}s</option>)}
      </select>
    </label>
    <Button variant="secondary" disabled={enabled === null} onClick={() => { const next = !enabled; void api.saveSuperReactionSetting({ enabled: next }).then((value) => setEnabled(value.enabled)); }}>{enabled ? 'Disable globally' : 'Enable globally'}</Button>
  </Card>;
}

function LobbyManagement() {
  const [lobbies, setLobbies] = useState<AdminLobby[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.adminLobbies();
      setLobbies(result.lobbies);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load lobbies.');
    }
  }, []);

  useEffect(() => void load(), [load]);

  const remove = async (lobby: AdminLobby) => {
    if (!window.confirm(
      `Delete “${lobby.name}” (${lobby.code})?\n\nThis permanently removes its entries and game data.`,
    )) return;
    setBusyId(lobby.id);
    setError(null);
    try {
      await api.deleteLobby(lobby.id);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not delete that lobby.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="mb-6 p-0">
      <details>
        <summary className="cursor-pointer px-4 py-3 font-medium">
          Lobbies{lobbies ? ` (${lobbies.length})` : ''}
        </summary>
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs text-text-faint">
            Empty lobbies close after 24 hours and are deleted after 30 days.
          </p>
        </div>
        {error && <div className="mx-4 mb-3"><Banner tone="danger">{error}</Banner></div>}
        {lobbies === null ? (
          <p className="px-4 pb-3 text-sm text-text-faint">Loading…</p>
        ) : lobbies.length === 0 ? (
          <p className="px-4 pb-3 text-sm text-text-faint">No lobbies yet.</p>
        ) : (
          <ul className="divide-y divide-border px-4 pb-3">
            {lobbies.map((lobby) => (
              <li key={lobby.id} className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{lobby.name} <span className="font-mono text-xs text-text-faint">{lobby.code}</span></p>
                  <p className="text-xs text-text-faint">
                    {lobby.status} · hosted by {lobby.hostName} · {lobby.memberCount} member{lobby.memberCount === 1 ? '' : 's'} · {lobby.connectedCount} online · created {relativeTime(lobby.createdAt)}
                  </p>
                </div>
                <Button variant="danger" loading={busyId === lobby.id} disabled={busyId !== null} onClick={() => void remove(lobby)}>
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )}
      </details>
    </Card>
  );
}

function VersionSetting({ onChange }: { onChange: (version: string) => void }) {
  const [version, setVersion] = useState('');
  const [savedVersion, setSavedVersion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.version().then(({ version: current }) => {
      setVersion(current);
      setSavedVersion(current);
    }).catch(() => setError('Could not load the version.'));
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { version: next } = await api.saveVersion(version);
      setVersion(next);
      setSavedVersion(next);
      onChange(next);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the version.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6">
      <form onSubmit={(event) => void save(event)} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field
            label="Version label"
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            maxLength={40}
          />
          {error && <div className="mt-2"><Banner tone="danger">{error}</Banner></div>}
        </div>
        <Button type="submit" loading={busy} disabled={!version.trim() || version.trim() === savedVersion}>
          Save version
        </Button>
      </form>
    </Card>
  );
}

function SiteTitleSetting({ onChange }: { onChange: (title: string) => void }) {
  const [title, setTitle] = useState('');
  const [savedTitle, setSavedTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.siteConfig()
      .then(({ title: current }) => {
        setTitle(current);
        setSavedTitle(current);
      })
      .catch(() => setError('Could not load the browser tab title.'));
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { title: next } = await api.saveSiteTitle(title);
      setTitle(next);
      setSavedTitle(next);
      onChange(next);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the browser tab title.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-6">
      <form onSubmit={(event) => void save(event)} className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Field
            label="Browser tab title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={60}
          />
          {error && <div className="mt-2"><Banner tone="danger">{error}</Banner></div>}
        </div>
        <Button type="submit" loading={busy} disabled={!title.trim() || title.trim() === savedTitle}>
          Save title
        </Button>
      </form>
    </Card>
  );
}

export function Admin({
  onBack,
  onVersionChange,
  onSiteTitleChange,
}: {
  onBack: () => void;
  onVersionChange: (version: string) => void;
  onSiteTitleChange: (title: string) => void;
}) {
  const { session } = useSession();
  const [members, setMembers] = useState<AdminMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<{ code: string; label: string } | null>(null);
  const [games, setGames] = useState<GameHistoryEntry[]>([]);

  const load = useCallback(async () => {
    try {
      const { members: list } = await api.listMembers();
      setMembers(list);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load members.');
    }
  }, []);

  useEffect(() => {
    void load();
    void api.adminGameHistory().then(({ games: history }) => setGames(history));
  }, [load]);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  function onCreate(event: FormEvent) {
    event.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;

    void run(async () => {
      const created = await api.createMember(trimmed);
      setReveal({ code: created.code, label: created.label });
      setLabel('');
    });
  }

  function onRotate(member: AdminMember) {
    const confirmed = window.confirm(
      `Issue ${member.displayName} a new code?\n\n` +
        `Their old code stops working and they will be signed out everywhere.`,
    );
    if (!confirmed) return;

    void run(async () => {
      const rotated = await api.rotateCode(member.id);
      setReveal({ code: rotated.code, label: member.displayName });
    });
  }

  /**
   * Renaming somebody's handle.
   *
   * The username is what keeps the room legible, so it is an admin action —
   * and somebody will eventually pick something unreadable. Stats key on the
   * user id, so this breaks nothing retroactively, which is the whole reason
   * the id exists.
   */
  function onRename(member: AdminMember) {
    const next = window.prompt(
      `Rename ${member.username}?\n\nTheir display name stays the same.`,
      member.username,
    );
    if (next === null || next.trim() === member.username) return;

    void run(() => api.renameMember(member.id, next.trim()).then(() => undefined));
  }

  function onRevoke(member: AdminMember) {
    const confirmed = window.confirm(
      `Revoke ${member.displayName}'s access?\n\n` +
        `They will be signed out and need a new code to get back in. Their stats are kept.`,
    );
    if (!confirmed) return;
    void run(() => api.revokeCode(member.id).then(() => undefined));
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Members</h1>
          <p className="text-sm text-text-muted">
            {members?.length ?? 0} in the club
          </p>
        </div>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
      </div>

      {error && (
        <div className="mb-4">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      <FeatureRequestsAdmin />

      <LobbyManagement />

      <AdminGuests />

      <SiteTitleSetting onChange={onSiteTitleChange} />

      <VersionSetting onChange={onVersionChange} />

      <DefaultSettings />
      <GlobalFeatures />

      <Appearance />

      <GameHistory games={games} title="Complete game archive" collapsible />

      <Card className="mb-6">
        <form onSubmit={onCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field
              label="Add someone"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Ben"
              maxLength={40}
            />
          </div>
          <Button type="submit" loading={busy} disabled={!label.trim()}>
            Generate code
          </Button>
        </form>
      </Card>

      {members === null ? (
        <Spinner label="Loading members" />
      ) : (
        <ul className="flex flex-col gap-2">
          {members.map((member) => (
            <li key={member.id}>
              <Card className="flex flex-wrap items-center gap-3">
                <Avatar value={member.avatarValue} />

                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-medium">
                    <span className="truncate">{member.displayName}</span>
                    {member.username !== member.displayName && (
                      <span className="truncate text-xs text-text-faint">{member.username}</span>
                    )}
                    {member.isAdmin && (
                      <span className="rounded bg-accent/15 px-1.5 py-0.5 text-xs text-accent">
                        admin
                      </span>
                    )}
                    {member.id === session?.user.id && (
                      <span className="text-xs text-text-faint">you</span>
                    )}
                  </p>
                  <p className="text-sm text-text-faint">
                    {!member.hasActiveCode
                      ? 'No active code — revoked'
                      : !member.isSetUp
                        ? 'Has not signed in yet'
                        : `Last seen ${relativeTime(member.lastSeenAt)}`}
                    {member.extraSlotsReceived > 0 &&
                      ` · ${member.extraSlotsReceived} extra slot${member.extraSlotsReceived === 1 ? '' : 's'}`}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => onRotate(member)} disabled={busy}>
                    New code
                  </Button>
                  {member.id !== session?.user.id && (
                    <Button variant="danger" onClick={() => onRevoke(member)} disabled={busy}>
                      Revoke
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => onRename(member)} disabled={busy}>
                    Rename
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {reveal && (
        <CodeReveal
          code={reveal.code}
          label={reveal.label}
          onDone={() => setReveal(null)}
        />
      )}
    </main>
  );
}
